import { computed, ref } from 'vue';

export default function useNotionDatabase(notionClient) {
  let workspaceName = '';
  const databases = ref({});
  const pages = ref({});
  const loading = ref(false);

  const getTitle = (node) => {
    if (node.title[0] === undefined) {
      return 'Untitled';
    }
    return node.title[0].plain_text;
  };

  const extractParent = async (database) => {
    try {
      const parent = database.parent;
      if (parent.type === 'page_id') {
        let response = pages.value[parent.page_id];
        if (response === undefined) {
          response = await notionClient.client.value.pages.retrieve({ page_id: parent.page_id });
        }

        const name = Object.values(response.properties).reduce((res, next) => {
          if (next.type === 'title') {
            return getTitle(next);
          }
          return res;
        }, '');

        return {
          ...database,
          parentName: name,
          isGrandParentWorkspace: response.parent.type === 'workspace',
        };
      }

      if (parent.type === 'database_id') {
        let response = databases.value[parent.database_id];
        if (response === undefined) {
          response = await notionClient.client.value.databases.retrieve({ database_id: parent.database_id });
          response = { parent: response.parent, name: getTitle(response) };
        }
        return {
          ...database,
          parentName: response.name,
          isGrandParentWorkspace: response.parent.type === 'workspace',
        };
      }

      if (parent.type === 'workspace') {
        return { ...database, parentName: workspaceName, isGrandParentWorkspace: false };
      }

      return { ...database, parentName: '', isGrandParentWorkspace: false };
    } catch (error) {
      return { ...database, parentName: '', isGrandParentWorkspace: false };
    }
  };

  const formatDatabase = (original) => ({
    archived: original.archived,
    id: original.id,
    name: getTitle(original),
    properties: original.properties,
    parentType: original.parent.type,
    parent: original.parent,
    icon: original.icon,
  });

  const init = async (workspace) => {
    loading.value = true;
    databases.value = {};
    workspaceName = workspace.name;

    const responseDatabases = await notionClient.client.value.search({
      filter: {
        value: 'database',
        property: 'object',
      },
    }); // TODO: next_cursor
    for (let i = 0; i < responseDatabases.results.length; i++) {
      const database = responseDatabases.results[i];
      databases.value[database.id] = formatDatabase(database);
    }
    const responsePages = await notionClient.client.value.search({ filter: { value: 'page', property: 'object' } }); // TODO: next_cursor

    for (let i = 0; i < responsePages.results.length; i++) {
      const page = responsePages.results[i];
      pages.value[page.id] = page;
    }

    for (let i = 0; i < responseDatabases.results.length; i++) {
      const databaseId = responseDatabases.results[i].id;
      // eslint-disable-next-line no-await-in-loop
      databases.value[databaseId] = await extractParent(databases.value[databaseId]);
    }

    loading.value = false;
  };

  const searchByNameLoading = ref(false);
  const searchByName = async (name) => {
    searchByNameLoading.value = true;
    const response = await notionClient.client.value.search({
      query: name,
      filter: { value: 'database', property: 'object' },
    }); // TODO: next_cursor

    const result = {};
    for (let i = 0; i < response.results.length; i++) {
      const database = response.results[i];
      // eslint-disable-next-line no-await-in-loop
      result[database.id] = await extractParent(formatDatabase(database));
    }

    searchByNameLoading.value = false;
    return Object.values(result);
  };

  const getSingle = async (id) => {
    if (databases.value[id] !== undefined) {
      return databases.value[id];
    }
    try {
      const response = await notionClient.client.value.databases.retrieve({ database_id: id });
      return await extractParent(formatDatabase(response));
    } catch (error) {
      return undefined;
    }
  };

  const getSingleNoParent = async (id) => {
    if (databases.value[id] !== undefined) {
      return databases.value[id];
    }
    try {
      const response = await notionClient.client.value.databases.retrieve({ database_id: id });
      return formatDatabase(response);
    } catch (error) {
      return undefined;
    }
  };

  const savePage = async (id, properties, content = [], icon = null, cover = null) => notionClient.client.value.pages.create({
    parent: { database_id: id },
    properties,
    children: content,
    icon,
    cover,
  });

  return {
    databases: computed(() => Object.values(databases.value)),
    init,
    getSingle,
    getSingleNoParent,
    savePage,
    searchByName,
    searchByNameLoading,
    loading,
  };
}
